import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import useApi from 'hooks/useApi';
import { useParams } from 'react-router-dom';
import useUserRoles from 'hooks/useUserRoles';
import UserApiService from 'api/UserApiService';
import SelectUser from 'components/SelectUser';
import { useTranslation } from 'react-i18next';
// import { handleApiError } from 'api/errors';
import Order from 'types/entities/Order';
import OrderScope from 'types/entities/OrderScope';
// import useBaseLineOrderWorkunitApi from '../services/useBaseLineOrderWorkunitApi';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import User from 'types/entities/User';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
// import OrderScopeApiService from 'api/OrderScopeApiService';
// import { ChildRefBLLastpageType } from 'features/ChangeBaseline/ChangeBaseline';
import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import { SearchParams } from 'api/ResourceAPI';

// Define your route params
type RouteParams = {
  order_id: string;
};

// Define your component props
type BaselineOrderSummaryProps = {
  currentStep: number;
  handleNext: React.Dispatch<React.SetStateAction<any>>;
  handleCancel: React.Dispatch<React.SetStateAction<any>>;
};

interface WorkunitPayload {
  delivery_manager?: User[]; // or whatever type foundId?.accountable has
  // other props...
}

const BaselineOrderSummary = forwardRef((_props: BaselineOrderSummaryProps, _ref) => {
  const { currentStep, handleNext, handleCancel } = _props;
  // const summaryRef = useRef<HTMLDivElement>(null);
  const { order_id } = useParams<RouteParams>();
  // useImperativeHandle(ref, () => ({
  //   scrollToSummary: () => {
  //     summaryRef.current?.scrollIntoView({ behavior: 'smooth' });
  //   },
  // }));
  const { makeCall } = useApi();
  const [t] = useTranslation();
  const appState = useSelector((state) => state.app);
  // const sourceOrder = useSelector((state) => state.changeBaseline?.sourceOrder);
  const { baseLineOrder, sourceOrder } = useSelector((state) => state.changeBaseline);
  const userRoles = useUserRoles();
  const { dispatch } = useAppState();
  const [displayed, setDisplayed] = useState<OrderScope[]>([]);
  const [order, setOrder] = useState<Order | Partial<Order> | null>(null);
  // const [workunit, setWorkunit] = useState<WorkunitPayload>({});
  const [orderDetails, setOrderDetails] = useState<{ totalPrice: number; totalCharge: number }>({
    totalPrice: 0,
    totalCharge: 0,
  });
  const [workunitItems, setWorkunitItems] = useState<any[]>([]);
  // const { fetchBaselineOrderSummaryPage } = useBaseLineOrderWorkunitApi();

  useEffect(() => {
    if (order_id && appState.roles) {
      dispatch(addLoadingAction('api'));
      const fetchOrderScopes = async () => {
        try {
          const searchParams: SearchParams = {
            // order_id: parseInt(match.params.order_id, 10),
            // order_id: baseLineOrder?.id,
            size: 10000,
            page: 0,
            is_archived: false,
            join: ['scope', 'consultant', 'client', 'delivery_manager'],
            // workunit_id: '>=0', // custom value so removed here
          };

          const res_dm = await UserApiService.fetchPossiblesTeamLeaders();
          let filteredDeliveryManagers = res_dm;
          if (order && order?.tmaccountables && order?.tmaccountables.length > 0) {
            const idsToFilter = order?.tmaccountables.map((data) => data.id);
            filteredDeliveryManagers = res_dm?.filter((data) => idsToFilter.includes(data.id));
          }

          const { datas } = await makeCall(
            ChangeBaseLineAPIServices.fetchBaseLineAffectationMissions(Number(baseLineOrder?.id), 3, searchParams),
            'Unable to retrieve order workunits',
            'affectationGrid'
          );

          const result: OrderScope[] | undefined = await ChangeBaseLineAPIServices.fetchOrderScopeBLById(
            Number(baseLineOrder?.id),
            {
              join: ['scope', 'accountable'],
            }
          );

          setWorkunitItems(datas);
          const deliveryManagerObj: Record<number, User | null> = {};

          datas?.forEach((item: any) => {
            const fullName = item.default_delivery_manager_id;

            const matchedUser = filteredDeliveryManagers?.find((user) => user.id === fullName);

            deliveryManagerObj[item.scope_id] = matchedUser ?? null;
          });
          // Always add the key to each row
          result?.forEach((row) => {
            const user = deliveryManagerObj[row.scope_id] ?? null;
            (row as any).accountable = user;
            (row as any).accountable_id = user?.id ?? null;
          });
          if (result) {
            setDisplayed(result as OrderScope[]);
            setOrderDetails({
              totalPrice: result.reduce((acc, curr) => acc + curr.price, 0),
              totalCharge: result.reduce((acc, curr) => acc + curr.charge, 0),
            });
          }
          dispatch(removeLoadingAction('api'));
        } catch (error) {
          const result = (error as Error).message;
          dispatch(removeLoadingAction('api'));
          if (result) {
            dispatch(
              setSnackbarAction({ message: `Failed to retrieve order  : ${result}`, open: true, severity: 'error' })
            );
          } else dispatch(setSnackbarAction({ message: 'Failed to retrieve order ', open: true, severity: 'error' }));
        } finally {
          dispatch(removeLoadingAction('api'));
        }
      };
      fetchOrderScopes();
      // fetchOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id]);

  // useEffect(() => {
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   if (rowId) {
  //     handleSubmitBLSummary();
  //   }
  // }, [rowId]);

  const handleSubmitBLSummary = async (accountable: any, id: number, row: any) => {
    const selectedItems = workunitItems
      ?.filter((item: any) => item.scope_id === row.scope_id)
      .map((item: any) => item.id);

    const baseLinePara = {
      page_typeid: 3,
      workunit_type: 'multiple',
      order_baseline_id: Number(baseLineOrder?.id),
      source_order_id: Number(order_id),
      baseline_version:
        sourceOrder?.baseline_version === 0 || sourceOrder?.baseline_version == null
          ? 1
          : sourceOrder?.baseline_version + 1,
    };
    const toSend: {
      ressources: number[];
      data: {
        order_baseline_id: number;
        source_order_id: number;
        status: number;
        default_delivery_manager_id: number;
      };
    } = {
      ressources: selectedItems,
      data: {
        order_baseline_id: Number(baseLineOrder?.id),
        source_order_id: Number(order_id),
        status: 1,
        default_delivery_manager_id: accountable?.id,
      },
    };
    try {
      const res = await ChangeBaseLineAPIServices.bulkUpdate(
        baseLinePara,
        toSend.ressources,
        toSend.data as any,
        false
      );
      if (res) dispatch(setSnackbarAction({ message: 'Saved', open: true, severity: 'success', duration: 500 }));
    } catch (error) {
      const result = (error as Error).message;
      if (result) {
        dispatch(
          setSnackbarAction({ message: `Unable to save affectations: ${result}`, open: true, severity: 'error' })
        );
      } else {
        dispatch(
          setSnackbarAction({ message: `Unable to save affectations: ${error}`, open: true, severity: 'error' })
        );
      }
    }
  };

  const devises = useSelector((state) => state.app.devises);
  const [devise_name] = displayed?.map((row) => row?.devise_name);

  const affectedDevise =
    devise_name && devise_name.length > 0 ? devises?.find((devise) => devise_name[0] === devise.name)?.symbol : null;

  return (
    <Box sx={{ mb: 5, mt: 5 }}>
      {/* <div className="bg-gray-100 p-4 w-full">{order && <NewBaseLineOrderWorkunits order={order} />}</div> */}{' '}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <TableContainer component={Box} sx={{ mb: 4 }}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('Scope')}</TableCell>
                  {/* <TableCell align="center">{t('Quantity')}</TableCell> */}
                  <TableCell align="center">{t('Validated_By')}</TableCell>
                  {/* <TableCell align="center">{t('Ref_Order_Items')}</TableCell> */}
                  <TableCell align="center">{t('Po_Ref')}</TableCell>
                  <TableCell align="center">{t('Charge')}</TableCell>
                  <TableCell align="center">{t('Price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    order={order}
                    devise={affectedDevise || ''}
                    delivery_coordinators={baseLineOrder?.tmaccountables || []}
                    userIsClient={userRoles.isCustomer}
                    userIsDC={userRoles.isDeliveryCoordinator}
                    userIsDM={userRoles.isDeliveryManager}
                    userIsBu_support={userRoles.isBu_support}
                    setRefOrder={(e, id) =>
                      setDisplayed(
                        displayed.map((displayedRow) =>
                          displayedRow.id !== id ? displayedRow : { ...displayedRow, ref_order: e?.target?.value || '' }
                        )
                      )
                    }
                    setAccountable={(accountable: User | null, id: number) => {
                      handleSubmitBLSummary(accountable, id, row);
                      setDisplayed(
                        displayed.map((displayedRow) =>
                          // eslint-disable-next-line no-nested-ternary
                          displayedRow.id !== id
                            ? displayedRow
                            : accountable
                            ? { ...displayedRow, accountable, accountable_id: accountable.id }
                            : { ...displayedRow, accountable: null, accountable_id: null }
                        )
                      );
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex' }}>
            <Box sx={{ ml: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ mr: 2 }}>{t('Total_Price')}</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {orderDetails?.totalPrice?.toFixed(2) || 0}
                  {affectedDevise && affectedDevise !== 'E' ? affectedDevise?.toLowerCase() : '€'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ mr: 2 }}>{t('Total_Charge')}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{orderDetails.totalCharge?.toFixed(2)}</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default BaselineOrderSummary;

function Row(props: {
  row: OrderScope;
  delivery_coordinators: User[];
  userIsClient: boolean;
  userIsDC: boolean;
  userIsDM: boolean;
  userIsBu_support: boolean;
  devise: string;
  setRefOrder: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => void;
  setAccountable: (accountable: User, id: number) => void;
  order: Order | Partial<Order> | null;
}) {
  const { row, delivery_coordinators, setRefOrder, setAccountable, userIsClient, userIsDC, userIsBu_support } = props;
  // console.log(row);

  const devises = useSelector((state) => state.app.devises);
  const [t] = useTranslation();
  const currentDevise = devises?.find((devise) => row?.devise_name[0] === devise.name);

  interface PODetail {
    purchase_order_id: number | null;
    purchase_order: string | null;
    price: number;
    charge?: number;
  }

  // Override the interface typing since po_details is actually an object, not a string
  const poDetailsObject = row.po_details as unknown as Record<string, PODetail>;
  const poEntries = Object.entries(poDetailsObject || {});

  // Group by PO Ref (purchase_order) and sum workload/price
  const groupedPOs = useMemo(() => {
    const groups: Record<
      string,
      {
        purchase_order: string;
        totalPrice: number;
        totalCharge: number;
        count: number;
      }
    > = {};

    poEntries.forEach(([index, item]: [string, any]) => {
      const poRef = item.purchase_order || 'empty';

      if (!groups[poRef]) {
        groups[poRef] = {
          purchase_order: poRef,
          totalPrice: 0,
          totalCharge: 0,
          count: 0,
        };
      }

      groups[poRef].totalPrice += item.price || 0;
      groups[poRef].totalCharge += item.charge || 0;
      groups[poRef].count += 1;
    });

    return Object.values(groups);
  }, [poEntries]);

  const displayPORef = (purchaseOrder: string) => {
    if (!purchaseOrder || purchaseOrder === 'empty' || purchaseOrder === 'null' || purchaseOrder === '-') {
      return 'none';
    }
    return purchaseOrder;
  };

  // If no PO details, use the original design
  if (groupedPOs.length === 0) {
    return (
      <>
        <TableRow>
          <TableCell />
          <TableCell component="th" scope="row">
            {row?.scope?.name ? t(row?.scope?.name) : 'Not defined'}
          </TableCell>
          <TableCell align="center" width="20%">
            <SelectUser
              users={delivery_coordinators}
              selectedUser={row?.accountable}
              disabled={userIsClient || userIsDC || userIsBu_support}
              onSelectUser={(value) => setAccountable(value as User, row.id)}
            />
          </TableCell>
          <TableCell align="center">
            <Input
              value={row.ref_order || ''}
              onChange={(e) => setRefOrder(e, row.id)}
              required
              disabled={userIsClient || userIsDC || userIsBu_support}
              sx={{ border: '1px solid', borderColor: 'neutral.main', display: 'none' }}
            />
            none
          </TableCell>
          <TableCell align="center">-</TableCell>
          <TableCell align="center">-</TableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: '#b1b1b12b' }}>
          <TableCell colSpan={4} align="center" />
          <TableCell align="center">
            <Typography sx={{ fontWeight: 'bold' }}>{(row?.charge ?? 0).toFixed(2)}</Typography>
          </TableCell>
          <TableCell align="center">
            <Typography sx={{ fontWeight: 'bold' }}>
              {(row?.price ?? 0).toFixed(2)}
              {currentDevise?.symbol && currentDevise?.symbol !== 'E' ? currentDevise?.symbol?.toLowerCase() : '€'}
            </Typography>
          </TableCell>
        </TableRow>
      </>
    );
  }

  return (
    <>
      {/* Show all PO rows with scope name and user selection only on first row */}
      {groupedPOs.map((groupedPO, arrayIndex) => (
        <TableRow key={`${row.id}-${groupedPO.purchase_order}`}>
          <TableCell />
          <TableCell component="th" scope="row">
            {/* Show scope name only on first PO row */}
            {arrayIndex === 0 && (row?.scope?.name ? t(row.scope.name) : 'Not defined')}
          </TableCell>
          <TableCell align="center" width="20%">
            {/* Show user selection only on first PO row */}
            {arrayIndex === 0 && (
              <SelectUser
                users={delivery_coordinators}
                selectedUser={row?.accountable}
                disabled={userIsClient || userIsDC || userIsBu_support}
                onSelectUser={(value) => setAccountable(value as User, row.id)}
              />
            )}
          </TableCell>
          <TableCell align="center">
            {/* Hidden input only on first PO row */}
            {arrayIndex === 0 && (
              <Input
                value={row.ref_order || ''}
                onChange={(e) => setRefOrder(e, row.id)}
                required
                disabled={userIsClient || userIsDC || userIsBu_support}
                sx={{ border: '1px solid', borderColor: 'neutral.main', display: 'none' }}
              />
            )}
            {/* Show individual PO Ref */}
            {displayPORef(groupedPO.purchase_order)}
          </TableCell>
          <TableCell align="center">
            {/* Individual PO charge */}
            {groupedPO.totalCharge.toFixed(2)}
          </TableCell>
          <TableCell align="center">
            {/* Individual PO price */}
            {groupedPO.totalPrice.toFixed(2)}
            {currentDevise?.symbol && currentDevise?.symbol !== 'E' ? currentDevise?.symbol?.toLowerCase() : '€'}
          </TableCell>
        </TableRow>
      ))}

      {/* Summary row with bold values */}
      <TableRow sx={{ backgroundColor: '#b1b1b12b' }}>
        <TableCell colSpan={4} align="center" />
        <TableCell align="center">
          <Typography sx={{ fontWeight: 'bold' }}>{(row?.charge ?? 0).toFixed(2)}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography sx={{ fontWeight: 'bold' }}>
            {(row?.price ?? 0).toFixed(2)}
            {currentDevise?.symbol && currentDevise?.symbol !== 'E' ? currentDevise?.symbol?.toLowerCase() : '€'}
          </Typography>
        </TableCell>
      </TableRow>
    </>
  );
}
